import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowRight, Users, UserPlus, UserCheck, PieChart, TrendingUp, Calendar } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';
import StatCard from './ui/StatCard';
import SectionCard from './ui/SectionCard';
import ProgressBar from './ui/ProgressBar';
import DataTable from './ui/DataTable';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';

const USER_TYPE_CATEGORIES = [
    { value: 'PET_GUARDIAN', label: 'Pet Guardian', color: 'bg-purple-500', fill: '#a855f7' },
    { value: 'VETERINARIAN', label: 'Vet', color: 'bg-cyan-500', fill: '#06b6d4' },
    { value: 'SERVICE_PROVIDER', label: 'Service Provider', color: 'bg-orange-500', fill: '#f97316' },
    { value: 'FIRM', label: 'Firm', color: 'bg-[#1B3A6B]', fill: '#1B3A6B' }
];

const RECENT_USER_COLUMNS = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'type', header: 'Type' },
    {
        key: 'created_at',
        header: 'Registered',
        width: '140px',
        render: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A')
    }
];

const UsersTab = ({ filters, onViewDetailed }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const loadUserStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getUserStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load user stats:', err);
            setError(err.message || 'Failed to load user statistics');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadUserStats();
    }, [loadUserStats]);

    const userTypeData = useMemo(() => {
        return USER_TYPE_CATEGORIES.map((category) => ({
            name: category.label,
            value: stats?.usersByType?.[category.value] || 0,
            color: category.fill
        }));
    }, [stats?.usersByType]);

    const breakdownTotal = useMemo(() =>
        USER_TYPE_CATEGORIES.reduce(
            (sum, category) => sum + (stats?.usersByType?.[category.value] || 0),
            0
        ),
    [stats?.usersByType]);

    const graphData = stats?.graph_data || [];

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadUserStats} className="retry-button">Retry</button>
            </div>
        );
    }

    return (
        <div className="sd-container">
            <div className="sd-detailed-link-row">
                <button
                    onClick={onViewDetailed}
                    className="sd-detailed-link"
                >
                    Detailed <ArrowRight size={14} />
                </button>
            </div>

            {/* KPI Cards */}
            <div className="sd-grid sd-grid--kpi-users">
                <StatCard
                    icon={Users}
                    label="Total Users"
                    value={stats?.totalUsers || 0}
                    trend={stats?.userGrowth}
                    trendLabel=""
                    color="bg-blue-500"
                    loading={loading}
                />
                <StatCard
                    icon={UserCheck}
                    label="Active Users"
                    value={stats?.activeUsers || 0}
                    subtitle={`${(stats?.activePercentage || 0).toFixed(1)}% of total`}
                    color="bg-emerald-500"
                    loading={loading}
                />
                <StatCard
                    icon={UserPlus}
                    label="New Users"
                    value={stats?.newUsers || 0}
                    subtitle="In range"
                    color="bg-violet-500"
                    loading={loading}
                />
            </div>

            {/* Charts */}
            {!loading && (
                <div className="sd-grid sd-grid--charts">
                    <SectionCard title="User Distribution" icon={PieChart}>
                        <CustomDonutChart
                            data={userTypeData}
                            totalValue={stats?.totalUsers || 0}
                            title=""
                        />
                    </SectionCard>
                    <SectionCard title="Registration Growth" icon={TrendingUp} className="sd-card--wide">
                        <CustomAreaChart data={graphData} color="#3b82f6" title="" />
                    </SectionCard>
                </div>
            )}

            {/* User Type Breakdown + Recent Registrations */}
            {!loading && (
                <div className="sd-grid sd-grid--split sd-grid--last">
                    <SectionCard title="User Type Breakdown" icon={Users}>
                        <div className="sd-progress-bar-list">
                            {USER_TYPE_CATEGORIES.map((category) => {
                                const count = stats?.usersByType?.[category.value] || 0;
                                return (
                                    <ProgressBar
                                        key={category.value}
                                        label={category.label}
                                        count={count}
                                        total={breakdownTotal || 1}
                                        color={category.color}
                                    />
                                );
                            })}
                        </div>
                    </SectionCard>

                    <SectionCard title="Recent Registrations" icon={Calendar}>
                        <DataTable
                            columns={RECENT_USER_COLUMNS}
                            data={stats?.recentUsers || []}
                            emptyText="No recent registrations"
                        />
                    </SectionCard>
                </div>
            )}
        </div>
    );
};

export default UsersTab;
